<template>
  <div 
    class="animated-backdrop" 
    @mousemove="handleMouseMove"
    ref="backdropRef"
  >
    <div class="gradient-blob blob-1" :style="blob1Style"></div>
    <div class="gradient-blob blob-2" :style="blob2Style"></div>
    <div class="gradient-blob blob-3" :style="blob3Style"></div>
    <div class="overlay"></div>
    <slot></slot>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';

const backdropRef = ref(null);
const mouseX = ref(0.5); // normalized 0-1
const mouseY = ref(0.5); // normalized 0-1

const handleMouseMove = (e) => {
  if (!backdropRef.value) return;
  const rect = backdropRef.value.getBoundingClientRect();
  // Normalize mouse position to 0-1 range
  mouseX.value = (e.clientX - rect.left) / rect.width;
  mouseY.value = (e.clientY - rect.top) / rect.height;
};

// Blob positions shift based on mouse - more dramatic movement
const blob1Style = computed(() => ({
  left: `${10 + mouseX.value * 30}%`,
  top: `${5 + mouseY.value * 35}%`,
}));

const blob2Style = computed(() => ({
  left: `${50 - mouseX.value * 25}%`,
  top: `${40 - mouseY.value * 30}%`,
}));

const blob3Style = computed(() => ({
  left: `${25 + mouseX.value * 35}%`,
  top: `${60 - mouseY.value * 40}%`,
}));
</script>

<style scoped>
.animated-backdrop {
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
}

.gradient-blob {
  position: absolute;
  border-radius: 50%;
  filter: blur(20px);
  opacity: 0.75;
  transition: all 3s cubic-bezier(0.25, 0, 0.25, 1);
  animation: float 20s cubic-bezier(0.25, 0, 0.25, 1) infinite;
  pointer-events: none;
}

.blob-1 {
  width: 550px;
  height: 550px;
  background: radial-gradient(circle, #ff7f50 0%, rgba(255, 127, 80, 0.5) 50%, transparent 70%);
  animation-delay: 0s;
}

.blob-2 {
  width: 650px;
  height: 650px;
  background: radial-gradient(circle, #ffa500 0%, rgba(255, 165, 0, 0.5) 50%, transparent 70%);
  animation-delay: -7s;
}

.blob-3 {
  width: 480px;
  height: 480px;
  background: radial-gradient(circle, #ff9f4a 0%, rgba(255, 159, 74, 0.5) 50%, transparent 70%);
  animation-delay: -14s;
}

.overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.15);
  pointer-events: none;
  z-index: 1;
}

/* Slotted content goes above overlay */
.animated-backdrop > :not(.gradient-blob):not(.overlay) {
  position: relative;
  z-index: 2;
}

@keyframes float {
  0%, 100% {
    transform: translate(0, 0) scale(1);
  }
  33% {
    transform: translate(50px, -60px) scale(1.15);
  }
  66% {
    transform: translate(-40px, 50px) scale(0.85);
  }
}

/* Reduce animation on low-power devices */
@media (prefers-reduced-motion: reduce) {
  .gradient-blob {
    animation: none;
    transition: none;
  }
}
</style>
